import { Trans, t } from "@lingui/macro";
import { OutgoingInvoiceTypeEnum } from "@src/__generated__/urql-graphql";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { sumBy } from "lodash";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useInvoiceDetailStore } from "../../../store";

export const Total = observer(function Total() {
  const { workspaceStore } = useStore();
  const store = useInvoiceDetailStore();
  const itemsCount = store.invoice?.invoiceItems.length ?? 0;
  const { stats } = store.invoice!;

  const showVAT: boolean = computed(() => {
    const wsCountry = workspaceStore.settings?.country;
    if (!wsCountry) return true;
    if (wsCountry.code !== "LT") return true;
    return wsCountry.id === store.invoice?.billingEntity.country.id;
  }).get();

  return (
    <div className="flex pr-8">
      <div className="uppercase w-1/2 font-bold">
        {store.invoice?.type === OutgoingInvoiceTypeEnum.CreditNote
          ? t`Credit note items total in invoice currency`
          : t`Invoice items total in invoice currency`}
      </div>
      <div className="flex flex-col w-1/2">
        <div className="flex justify-between mb-6">
          <span>
            {t`Without VAT`}
            <span className="pl-8 text-gray-600">
              {itemsCount} {t`item/s`}
            </span>
          </span>
          <span>
            {currency.formatByCurrency(
              stats.total_amount_in_currency,
              store.invoice?.currency!,
            )}
          </span>
        </div>

        {Boolean(store.invoice!.proformaInvoices.length) && (
          <div className="flex justify-between mb-6">
            <span>
              <Trans>Used from proforma</Trans>
            </span>
            <span>
              {currency.formatByCurrency(
                sumBy(
                  store.invoice!.proformaInvoices,
                  (item) => item.amount_in_currency,
                ),
                store.invoice!.currency,
              )}
            </span>
          </div>
        )}

        {showVAT &&
          store.sumInvoiceItemsVatByPercent.map(
            ({ vatPercent, vatAmount, vatPercentFormatted }) => (
              <div key={vatPercent} className="flex justify-between mb-6">
                <span>
                  <Trans>VAT</Trans>
                  &nbsp;({vatPercentFormatted})
                </span>
                <span>
                  {currency.formatByCurrency(
                    vatAmount,
                    store.invoice?.currency!,
                  )}
                </span>
              </div>
            ),
          )}

        {showVAT &&
          (store.sumInvoiceItemsVatByPercent.length === 0 ? (
            <div className="flex justify-between mb-6">
              <span>
                <Trans>VAT</Trans>
                &nbsp;-
              </span>
              <span>-</span>
            </div>
          ) : store.sumInvoiceItemsVatByPercent.length > 1 ? (
            <div className="flex justify-between mb-6">
              <span>
                <Trans>Total VAT</Trans>
              </span>
              <span>
                {currency.formatByCurrency(
                  stats.total_vat_in_currency,
                  store.invoice?.currency!,
                )}
              </span>
            </div>
          ) : undefined)}

        <div className="flex justify-between mb-6">
          <span>
            <Trans>Total with VAT</Trans>
          </span>
          <span>
            {currency.formatByCurrency(
              store.invoice?.stats.total_amount_with_vat_in_currency ?? 0,
              store.invoice?.currency!,
            )}
          </span>
        </div>

        {store.invoice?.isReconcilliationInvoice &&
          "total_amount_with_vat_in_currency_to_pay_from_proforma_payments" in
            store.invoice.stats && (
            <div className="flex justify-between">
              <span>
                <Trans>Remaining amount with VAT</Trans>
              </span>
              <span>
                {currency.formatByCurrency(
                  store.invoice.stats
                    .total_amount_with_vat_in_currency_to_pay_from_proforma_payments,
                  store.invoice?.currency!,
                )}
              </span>
            </div>
          )}
      </div>
    </div>
  );
});
